/**
 * Driver picker — searchable, grouped combobox replacing the native <select>.
 *
 * Implements the "Driver Dropdown" design canvas artboard:
 *   - trigger shows the selected driver's label AND its plain-language subtitle
 *   - groups are ordered by where the number comes from, moving outward from
 *     the user's own input: manual → rooms → history → budget → revenue →
 *     linked accounts → composed → labor
 *   - a Suggested group leads when sibling accounts in the same department
 *     already use a driver, with the count as the reason
 *   - each row carries a mono formula chip on the right
 *   - search, ↑↓ to move, ↵ to select, esc to close
 *   - inside the account detail pane (an ancestor marked
 *     data-driver-takeover-host) the open picker TAKES OVER the whole pane:
 *     full-height scrolling list, sticky search, back arrow — 18 options were
 *     unreadable through a 360px floating menu. Elsewhere it stays a floating
 *     menu anchored to the trigger.
 *
 * Tokens (--lodge-onyx-*, --lodge-orange-*) come from styles/theme.css, which
 * redefines them for dark mode — so no literal colors here.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { ArrowLeft, Check, ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import type { DriverType, ForecastAccount } from '../../types';
import {
  DRIVER_CATALOG,
  DRIVER_GROUPS,
  driverSearchText,
  getVersionDriverEntry,
  isDriverVisibleForVersion,
  isDriverApplicable,
  type DriverCatalogEntry,
} from './driverCatalog';

import { useForecastV3 } from '../../context/ForecastV3Context';

const SUGGESTED = 'Suggested';

/** Every scrolling ancestor of `el`, nearest first. */
function scrollAncestors(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') out.push(node);
    node = node.parentElement;
  }
  return out;
}

/** Visible bounds of the nearest scrolling ancestor, else the viewport. */
function scrollParentRect(el: HTMLElement): { top: number; bottom: number } {
  for (const node of scrollAncestors(el)) {
    if (node.scrollHeight > node.clientHeight + 2) {
      const r = node.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom };
    }
  }
  return { top: 0, bottom: ownerWindowOf(el).innerHeight };
}
/** Design spec: the option list scrolls at 360px, over ~92px of menu chrome. */
const MENU_MAX_HEIGHT = 452;
/** Never squeeze the menu below roughly two rows — flip above instead. */
const MENU_MIN_HEIGHT = 220;
/** Gap between the trigger and the menu. */
const MENU_OFFSET = 6;
/** Breathing room against the viewport edge. */
const VIEWPORT_MARGIN = 12;
/**
 * Above the assistant launcher (z-index 1200, AssistantLauncher.css). The
 * detail panel is `position: sticky`, which creates a stacking context the
 * menu cannot escape from — hence the body portal below.
 */
const MENU_Z_INDEX = 1300;

interface DriverSelectProps {
  value: DriverType;
  accountType: 'Revenue' | 'Expense';
  onChange: (next: DriverType) => void;
  disabled?: boolean;
  isSaving?: boolean;
  /** Sibling accounts — drives the Suggested group. */
  accounts?: ForecastAccount[];
  /** The account being edited (excluded from its own suggestions). */
  account?: ForecastAccount;
  /**
   * Replace the money-account catalog with another one (the Other Statistics
   * drivers). Entries are shown as given — no eligibility filtering, no
   * Suggested group — and `value` / `onChange` carry that catalog's ids.
   */
  catalog?: { entries: DriverCatalogEntry[]; groups: { name: string; hint: string }[] };
}

interface FlatRow {
  entry: DriverCatalogEntry;
  /** Overridden reason line for the Suggested group. */
  desc: string;
  group: string;
}

export function DriverSelect({
  value,
  accountType,
  onChange,
  disabled = false,
  isSaving = false,
  accounts = [],
  account,
  catalog,
}: DriverSelectProps) {
  const { state } = useForecastV3();
  const isBudget = state.currentVersion?.type === 'Budget';
  const referenceYear = state.currentVersion?.budgetReferenceYear;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // The menu lives inside .panel-body, which scrolls. Left to itself the menu
  // runs past the bottom of the panel and the list — and the keyboard-hint
  // footer under it — get clipped. Measure the room actually available on
  // every scroll/resize and size (or flip) the menu to fit.
  const menuRef = useRef<HTMLDivElement>(null);
  // Takeover host — the detail pane, when the picker lives inside one.
  const [host, setHost] = useState<HTMLElement | null>(null);
  // Whether a takeover host exists at all — decides the trigger's chevron.
  const [inPane, setInPane] = useState(false);
  useEffect(() => {
    setInPane(Boolean(rootRef.current?.closest('[data-driver-takeover-host]')));
  }, []);
  const [layout, setLayout] = useState<{
    placement: 'below' | 'above';
    menuMax: number;
    left: number;
    width: number;
    top: number;
    bottom: number;
  }>({ placement: 'below', menuMax: MENU_MAX_HEIGHT, left: 0, width: 0, top: 0, bottom: 0 });

  const eligible = useMemo(
    () => catalog
      ? catalog.entries
      : DRIVER_CATALOG.filter(d => isDriverApplicable(d.id, accountType) && isDriverVisibleForVersion(d.id, isBudget)).map(d => getVersionDriverEntry(d.id, isBudget, referenceYear)!),
    [catalog, accountType, isBudget, referenceYear]
  );

  // Suggested: drivers already in use on sibling accounts of the same
  // department and account type. Real usage counts, not a curated list — an
  // empty result simply drops the group.
  const suggested = useMemo<FlatRow[]>(() => {
    if (catalog || !account?.department) return [];
    const siblings = accounts.filter(
      a =>
        a.id !== account.id &&
        a.department === account.department &&
        a.accountType === account.accountType
    );
    if (siblings.length < 2) return [];
    const counts = new Map<string, number>();
    for (const s of siblings) {
      const t = s.inheritsFromDepartment && s.departmentDriverType ? s.departmentDriverType : s.driverType;
      if (!t || t === 'Manual') continue;
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([t]) => eligible.some(e => e.id === t))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([t, n]) => {
        const entry = eligible.find(e => e.id === t)!;
        return {
          entry,
          group: SUGGESTED,
          desc: `${n} of ${siblings.length} ${account.department} accounts use this`,
        };
      });
  }, [accounts, account, eligible, catalog]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (e: DriverCatalogEntry) => !q || driverSearchText(e).includes(q);

    const out: { name: string; hint: string; rows: FlatRow[] }[] = [];

    const suggestedRows = suggested.filter(r => match(r.entry));
    if (suggestedRows.length) {
      out.push({ name: SUGGESTED, hint: `used on sibling ${account?.department || ''} accounts`.trim(), rows: suggestedRows });
    }

    for (const g of catalog ? catalog.groups : DRIVER_GROUPS) {
      const rows = eligible
        .filter(e => e.group === g.name && match(e))
        .map(e => ({ entry: e, group: g.name, desc: e.desc }));
      if (rows.length) out.push({ name: g.name, hint: g.hint, rows });
    }
    return out;
  }, [eligible, suggested, query, account, catalog]);

  const flat = useMemo(() => groups.flatMap(g => g.rows), [groups]);
  const total = flat.length;
  // Suggested repeats drivers that also appear in their own group, so the
  // "n of N" chip counts DISTINCT drivers — otherwise it reads "21 of 19".
  const matchedCount = useMemo(() => new Set(flat.map(r => r.entry.id)).size, [flat]);
  const activeIdx = Math.min(active, Math.max(total - 1, 0));

  const selected = catalog ? catalog.entries.find(e => e.id === value) : getVersionDriverEntry(value, isBudget, referenceYear);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const openMenu = useCallback(() => {
    setHost(rootRef.current?.closest<HTMLElement>('[data-driver-takeover-host]') ?? null);
    setOpen(true);
  }, []);

  const takeover = open && host !== null;

  const pick = useCallback(
    (id: DriverType) => {
      close();
      if (id !== value) onChange(id);
    },
    [close, onChange, value]
  );

  // Close on outside click / Escape anywhere.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const doc = ownerDocumentOf(rootRef.current);
    doc.addEventListener('mousedown', onDown);
    doc.addEventListener('keydown', onKey);
    return () => {
      doc.removeEventListener('mousedown', onDown);
      doc.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Keep the portalled menu pinned to the trigger while the menu is open.
  //
  // The menu is position:fixed in a <body> portal, so it does not move with the
  // detail panel's scroll box on its own. Two mechanisms, deliberately:
  //   1. scroll listeners on the ACTUAL scrolling ancestors. A capture-phase
  //      listener on window is not enough — scroll events do not propagate, and
  //      it was verified to receive nothing while the panel's own scroll box
  //      scrolled the trigger 70px.
  //   2. a rAF diff loop, which also catches sticky reflow and content-height
  //      changes that fire no event. rAF is paused in a hidden tab, which is
  //      exactly when nobody is looking — (1) still covers the real cases.
  useEffect(() => {
    if (!open || host) return;
    let raf = 0;
    let prev = '';

    const win = ownerWindowOf(rootRef.current);
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const key = `${rect.top}|${rect.left}|${rect.width}|${win.innerHeight}`;
      if (key === prev) return;
      prev = key;

      // Scrolled out of its own scroll container → a fixed menu would float
      // over unrelated UI, so close rather than follow.
      const box = scrollParentRect(el);
      if (rect.bottom < box.top + 4 || rect.top > box.bottom - 4) {
        close();
        return;
      }

      const below = win.innerHeight - rect.bottom - MENU_OFFSET - VIEWPORT_MARGIN;
      const above = rect.top - MENU_OFFSET - VIEWPORT_MARGIN;
      const flip = below < MENU_MIN_HEIGHT && above > below;
      const room = flip ? above : below;
      setLayout({
        placement: flip ? 'above' : 'below',
        menuMax: Math.max(MENU_MIN_HEIGHT, Math.min(MENU_MAX_HEIGHT, room)),
        left: rect.left,
        width: rect.width,
        top: rect.bottom + MENU_OFFSET,
        bottom: win.innerHeight - rect.top + MENU_OFFSET,
      });
    };

    const tick = () => {
      raf = win.requestAnimationFrame(tick);
      measure();
    };

    measure();
    const scrollers = rootRef.current ? scrollAncestors(rootRef.current) : [];
    for (const node of scrollers) node.addEventListener('scroll', measure, { passive: true });
    win.addEventListener('resize', measure);
    win.addEventListener('scroll', measure, { passive: true });
    raf = win.requestAnimationFrame(tick);

    return () => {
      win.cancelAnimationFrame(raf);
      for (const node of scrollers) node.removeEventListener('scroll', measure);
      win.removeEventListener('resize', measure);
      win.removeEventListener('scroll', measure);
    };
  }, [open, host, total, close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Keep the highlighted row in view while arrowing.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[activeIdx];
      if (row) pick(row.entry.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  let rowIndex = -1;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => (open ? close() : openMenu())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="driver-select-trigger"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          minHeight: 46,
          padding: '6px 14px',
          border: `1px solid var(--lodge-onyx-${open ? '300' : '200'})`,
          borderRadius: 8,
          background: 'var(--lodge-white)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          boxSizing: 'border-box',
          textAlign: 'left',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>
            {selected?.label || value || 'Select a driver'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--lodge-onyx-500)', marginTop: 1 }}>
            {selected?.desc || 'Choose how this account is forecast'}
          </div>
        </div>
        {isSaving ? (
          <Loader2 size={16} className="animate-spin" style={{ color: 'var(--lodge-orange-400)' }} />
        ) : (
          // Inside the detail pane the picker pushes in as a full-pane view
          // (see takeover), so the affordance is "go right", not "drop down".
          inPane
            ? <ChevronRight size={16} style={{ color: 'var(--lodge-onyx-400)', flex: 'none' }} />
            : <ChevronDown size={16} style={{ color: 'var(--lodge-onyx-400)', flex: 'none' }} />
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className={takeover ? 'fv3-root driver-select-takeover' : 'fv3-root driver-select-menu'}
          style={takeover
            ? {
                // Takeover: cover the whole detail pane (header, tabs and
                // body) so the list gets the pane's full height.
                position: 'absolute',
                inset: 0,
                zIndex: 30,
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--lodge-white)',
              }
            : {
                // Portalled to <body>: the detail panel is position:sticky, so an
                // absolutely-positioned menu is trapped in its stacking context
                // (under the assistant launcher) and clipped by its scroll box.
                position: 'fixed',
                ...(layout.placement === 'below'
                  ? { top: layout.top }
                  : { bottom: layout.bottom }),
                left: layout.left,
                zIndex: MENU_Z_INDEX,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: layout.menuMax,
                width: layout.width,
                background: 'var(--lodge-white)',
                border: '1px solid var(--lodge-onyx-200)',
                borderRadius: 10,
                boxShadow: '0 12px 32px rgba(15,15,17,.14)',
                overflow: 'hidden',
              }}
        >
          {takeover && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flex: 'none',
                padding: '14px 16px 12px',
                borderBottom: '1px solid var(--lodge-onyx-100)',
              }}
            >
              <button
                type="button"
                onClick={close}
                aria-label="Back"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  border: 0,
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--lodge-onyx-600)',
                  cursor: 'pointer',
                  flex: 'none',
                }}
              >
                <ArrowLeft size={18} />
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Choose a driver</div>
                <div style={{ fontSize: 11.5, color: 'var(--lodge-onyx-500)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {account?.accountName ? `${account.accountName} · ` : ''}currently {selected?.label || value}
                </div>
              </div>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flex: 'none',
              padding: takeover ? '10px 16px' : '10px 12px',
              borderBottom: '1px solid var(--lodge-onyx-100)',
            }}
          >
            <Search size={15} style={{ color: 'var(--lodge-onyx-400)', flex: 'none' }} />
            <input
              ref={inputRef}
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search drivers, e.g. POR, budget, last year"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 'none',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                color: 'var(--lodge-onyx-900)',
                background: 'transparent',
              }}
            />
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--lodge-onyx-500)',
                border: '1px solid var(--lodge-onyx-100)',
                borderRadius: 4,
                padding: '2px 5px',
              }}
            >
              {matchedCount} of {eligible.length}
            </span>
          </div>

          <div
            ref={listRef}
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              padding: '4px 0',
            }}
          >
            {groups.map(group => (
              <div key={group.name} style={{ paddingBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 14px 4px' }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '.8px',
                      textTransform: 'uppercase',
                      color: 'var(--lodge-onyx-500)',
                    }}
                  >
                    {group.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--lodge-onyx-300)' }}>{group.hint}</div>
                </div>

                {group.rows.map(row => {
                  rowIndex += 1;
                  const idx = rowIndex;
                  const isSel = row.entry.id === value;
                  const isActive = idx === activeIdx;
                  return (
                    <div
                      key={`${group.name}:${row.entry.id}`}
                      data-row={idx}
                      role="option"
                      aria-selected={isSel}
                      onClick={() => pick(row.entry.id)}
                      onMouseEnter={() => setActive(idx)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: takeover ? '10px 20px 10px 16px' : '8px 20px 8px 14px',
                        cursor: 'pointer',
                        background: isActive ? 'var(--lodge-orange-50)' : 'transparent',
                        borderLeft: `2px solid ${isSel ? 'var(--lodge-orange-400)' : 'transparent'}`,
                      }}
                    >
                      <div style={{ width: 16, flex: 'none', paddingTop: 2, color: 'var(--lodge-orange-400)' }}>
                        {isSel && <Check size={16} strokeWidth={2.5} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, lineHeight: 1.35, fontWeight: 600, color: 'var(--lodge-onyx-900)', overflowWrap: 'anywhere' }}>
                          {row.entry.label}
                        </div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.4, color: 'var(--lodge-onyx-500)', marginTop: 1, overflowWrap: 'anywhere' }}>
                          {row.desc}
                        </div>
                      </div>
                      <span
                        style={{
                          flex: 'none',
                          marginTop: 2,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          fontWeight: 500,
                          color: 'var(--lodge-onyx-500)',
                          background: 'var(--lodge-onyx-50)',
                          borderRadius: 4,
                          padding: '3px 6px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.entry.tag}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            {total === 0 && (
              <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: 13, color: 'var(--lodge-onyx-500)' }}>
                No drivers match that search.
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flex: 'none',
              padding: '9px 14px',
              borderTop: '1px solid var(--lodge-onyx-100)',
              background: 'var(--lodge-onyx-50)',
              fontSize: 11,
              color: 'var(--lodge-onyx-400)',
            }}
          >
            <span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>↑↓</span> navigate
            </span>
            <span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>↵</span> select
            </span>
            <span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>esc</span> close
            </span>
          </div>
        </div>,
        host ?? ownerDocumentOf(rootRef.current).body
      )}
    </div>
  );
}
